import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { OktaAuthStateService } from '@okta/okta-angular';
import { AuthState } from '@okta/okta-auth-js';
import { BehaviorSubject, EMPTY, merge, Observable, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { RootScopeService } from './root-scope.service';

interface OriginalFailuresResponse {
  items: any[];
  totalCount: number;
}

/**
 * Lightweight "you have failures to review" indicator.
 *
 * Drives the unread-style badge next to the hamburger menu from the
 * totalCount of unreviewed "Processing Failure" original failures.
 *
 * totalCount already reflects only the *unreviewed* set, so the badge stays
 * until failures are officially reviewed on the Original Failures page — it
 * counts down as items are reviewed and disappears once the count hits 0.
 */
@Injectable({ providedIn: 'root' })
export class OriginalFailuresBadgeService {
  private static readonly REFRESH_INTERVAL_MS = 60_000;

  private readonly _count$ = new BehaviorSubject<number>(0);
  /** Number of unreviewed Processing Failure errors; 0 means the badge is hidden. */
  readonly count$: Observable<number> = this._count$.asObservable();

  private isAuthenticated = false;

  constructor(private http: HttpClient, private router: Router, private rootScope: RootScopeService,
    private oktaStateService: OktaAuthStateService) {
    // Keep the count current on navigation and on a periodic refresh. Back/forward is skipped
    // because it restores the cached page without going to the server; the timer covers it.
    let trigger: NavigationStart['navigationTrigger'];
    const navigated$ = this.router.events.pipe(
      filter(e => e instanceof NavigationStart || e instanceof NavigationEnd),
      filter(e => {
        if (e instanceof NavigationStart) {
          trigger = e.navigationTrigger;
          return false;
        }
        return trigger !== 'popstate';
      })
    );

    // Only poll while signed in to Okta: the API rejects requests with no access token.
    // Signing out or losing the token stops polling and hides the badge; signing in again
    // fetches straight away and resumes the periodic refresh.
    this.oktaStateService.authState$.pipe(
      map((state: AuthState) => !!state?.isAuthenticated),
      distinctUntilChanged(),
      tap(isAuthenticated => {
        this.isAuthenticated = isAuthenticated;
        if (!isAuthenticated) {
          this._count$.next(0);
        }
      }),
      switchMap(isAuthenticated => isAuthenticated
        ? merge(timer(0, OriginalFailuresBadgeService.REFRESH_INTERVAL_MS), navigated$)
        : EMPTY)
    ).subscribe(() => this.refresh());
  }

  /** Re-fetch the current customer's unreviewed Processing Failure count from the endpoint. */
  refresh(): void {
    if (!this.isAuthenticated) {
      this._count$.next(0);
      return;
    }
    // Failures are per customer, so there is nothing to count until one is selected.
    // Switching customer navigates, and the NavigationEnd refresh picks up the new one.
    const customerId = this.rootScope.globals?.currentCustomer;
    if (!customerId) {
      this._count$.next(0);
      return;
    }
    // NOTE: 'crosspointfailed' is the API's wire value for this status, not a
    // display string — it stays as-is even though the UI now says "Processing Failure".
    const url = environment.server.concat(
      `/get_original_failures?customer_cd=${encodeURIComponent(customerId)}&status=crosspointfailed&pageSize=1&pageIndex=0`
    );
    this.http.get<OriginalFailuresResponse>(url).subscribe({
      next: res => this._count$.next(res?.totalCount ?? 0),
      error: () => {
        // Leave the previously displayed count untouched on a failed poll.
      }
    });
  }
}
